<template>
  <v-dialog
    v-model="showModal"
    persistent
    max-width="900px"
    @keydown.enter.prevent="submit()"
    @keydown.esc.prevent="cancel()"
  >
    <el-overlay-loading :loading="pageloading" />
    <v-card class="modalCard">
      <v-card-title class="modalTitle pt-2 pb-1 mb-2">
        <h2>{{ sarasasTitle }}</h2>
      </v-card-title>
      <v-card-text class="pt-2 pb-2">
        <v-card class="mb-2 pb-4">
          <v-card-text>
            <v-row align="baseline" class="mb-0">
              <template v-if="showFail">
                <v-col cols="12">
                  <el-savitarna-alert dense outlined type="error">
                    {{ failMsg }}
                  </el-savitarna-alert>
                </v-col>
              </template>

              <v-col cols="12" md="8" v-if="isAdresas">
                <el-adresas
                  key="adresasSelect"
                  v-model="adresasSelect"
                  return-object
                  :label="$t('Adresas')"
                  :tooltip="`${ntrTooltipPrefix} Turtas. Adresas. Paieškai`"
                  :counter="1000"
                  autofocus
                />
              </v-col>
              <v-col cols="12" md="8" v-if="isRegistras">
                <el-text-field
                  key="registrasSelect"
                  v-model="registrasSelect"
                  :placeholder="regNrPlaceholder"
                  :label="$t('Registras')"
                  :tooltip="`${ntrTooltipPrefix} Turtas. Registras. Paieškai`"
                  autofocus
                />
              </v-col>
              <v-col cols="12" md="8" v-if="isUnikalus">
                <el-text-field
                  key="unikalusSelect"
                  :label="$t('Unikalus numeris')"
                  :tooltip="
                    `${ntrTooltipPrefix} Turtas. Unikalus numeris. Paieškai`
                  "
                  v-model="unikalusSelect"
                  autofocus
                />
              </v-col>

              <v-col cols="12" md="4" class="pb-2">
                <el-button
                  primary
                  @click.native="getData()"
                  :loading="loading"
                  :disabled="isSearching || !canSearch"
                  >{{ $t('Ieškoti') }}</el-button
                >
                <el-button
                  @click.native="cancelSearch()"
                  :loading="loading"
                  :disabled="isSearching"
                  >{{ $t('Išvalyti') }}</el-button
                >
              </v-col>
            </v-row>

            <v-data-table
              :headers="filteredHeaders"
              :items="itemsList"
              :item-class="rowClass"
              :loading="tableLoading"
              :search="search"
              :custom-filter="customSearch"
              :footer-props="{
                showFirstLastPage: true,
                showCurrentPage: true,
                itemsPerPageText: this.$t('Rodyti po:'),
                itemsPerPageAllText: this.$t('visi'),
                itemsPerPageOptions: itemsPerPageOptions,
              }"
              :hide-default-footer="hideFooter"
              :items-per-page="pageSize"
            >
              <template #top>
                <v-toolbar flat>
                  <v-text-field
                    v-model="search"
                    prepend-icon="mdi-magnify"
                    label=""
                    single-line
                    hide-details
                  ></v-text-field>
                </v-toolbar>
              </template>
              <template #[`item.registras`]="{ item }">
                <v-row dense no-gutters>
                  <v-col cols="12" md="12">
                    {{ item.tur_registras }}
                  </v-col>
                  <v-col cols="12" md="12">
                    {{ item.tur_identifikatorius }}
                  </v-col>
                </v-row>
              </template>
              <template #[`item.rusis`]="{ item }">
                <v-row dense no-gutters>
                  <v-col cols="12" md="12">
                    {{ item.tur_ntr_rusis }}
                  </v-col>
                  <v-col cols="12" md="12">
                    <span class="text-gray">{{ item.tur_ntr_adresas }}</span>
                  </v-col>
                </v-row>
              </template>
              <template #[`item.actions`]="{ item }">
                <div
                  v-for="dalis in item.dalys"
                  :key="`checkdv-${dalis.dal_value}`"
                >
                  <el-checkbox
                    v-if="item.canCheck"
                    :label="dalis.dal_pavadinimas"
                    :validate-key="`check-${dalis.dal_value}`"
                    :disabled="!dalis.enabled"
                    v-model="pasirinktosDalys"
                    :checkValue="dalis.dal_value"
                    :key="`check-${dalis.dal_value}`"
                    @click.native.stop="checkItem(dalis)"
                  />
                </div>
              </template>
              <template #[`item.dokumentas`]="{ item }">
                <div
                  v-for="dalis in item.dalys"
                  :key="`checkdok-${dalis.dal_value}`"
                >
                  {{ dalis.dal_dokumentas }}
                </div>
              </template>
              <template slot="loading">
                {{ $t('Kraunasi...') }}
              </template>
              <template slot="no-results">
                {{ $t('Duomenų nėra') }}
              </template>
              <template slot="no-data">
                {{ $t('Duomenų nėra') }}
              </template>
              <template #[`footer.page-text`]="props">
                {{ $t('Rodoma') }} {{ props.pageStart }}-{{ props.pageStop }}
                {{ $t('iš') }}
                {{ props.itemsLength }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-card-actions class="modalActions" elevation="5">
        <v-spacer></v-spacer>
        <el-button @click.native="cancelData()" :loading="loading">
          {{ $t('Uždaryti') }}
        </el-button>
        <el-button
          primary
          :disabled="!enableSubmit"
          :loading="loading"
          @click.native.stop="saveData()"
        >
          {{ $t('Įkelti') }}
        </el-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import busenaNtr from '@/mixins/busenaNtr'
import rowClass from '@/mixins/rowClass'
import tooltipNtr from '@/mixins/tooltipNtr'
import { regNrMaskFilter } from '@/mixins/regNrMask'

export default {
  name: 'ModalNtrTurtasSarasas',
  props: {
    value: Boolean,
    isAdresas: Boolean,
    isRegistras: Boolean,
  },
  components: {},
  mixins: [busenaNtr, rowClass, tooltipNtr],
  data() {
    return {
      pageloading: false,
      loading: false,
      tableLoading: false,
      adresasSelect: null,
      registrasSelect: null,
      unikalusSelect: null,
      showFail: false,
      failMsg: null,
      search: null,
      itemsPerPageOptions: [10, 50, -1],
      pageSize: 10,
      isSearching: false,
      turtai: [],
      pasirinktosDalys: [],
      ivestosDalys: [],
      nuzymetosDalys: [],
      headerCheck: false,
      regNrPlaceholder: '##/#########',
      regNrMask: regNrMaskFilter,
    }
  },
  computed: {
    ...mapGetters(['prasymasID']),
    showModal: {
      get() {
        return this.value
      },
      set(value) {
        this.adresasSelect = null
        this.registrasSelect = null
        this.unikalusSelect = null
        this.showFail = false
        this.resetSearch()
        this.$emit('input', value)
      },
    },
    canSearch() {
      return this.adresasSelect || this.registrasSelect || this.unikalusSelect
    },

    headers() {
      return [
        {
          text: this.$t('Registras ir unikalus numeris'),
          align: 'start',
          value: 'registras',
          width: 150,
        },
        {
          text: this.$t('Turto rūšis ir adresas'),
          align: 'start',
          value: 'rusis',
        },
        {
          text: this.$t('NT dalis'),
          align: 'start',
          value: 'actions',
        },
        {
          text: this.$t('Įregistruoto juridinio fakto duomenys'),
          align: 'start',
          value: 'dokumentas',
          hide: this.isPrasymasIregistravimas,
        },
      ]
    },
    filteredHeaders() {
      return this.headers.filter(item => {
        return !item.hide
      })
    },
    itemsList() {
      return this.turtai
        .map(item => {
          item.canCheck = item.enabled
          item.disabled = !item.enabled

          let search = ''
          item.dalys.forEach(dalis => {
            search += dalis.dal_pavadinimas + '‡' + dalis.dal_dokumentas
          })
          item.search = search

          return item
        })
        .sort((a, b) => {
          if (!a.enabled && b.enabled) return 1
          return 0
        })
    },
    hideFooter() {
      return this.itemsList.length <= 10
    },
    enableSubmit() {
      return this.pasirinktosDalys.length > 0 || this.nuzymetosDalys.length > 0
    },
    sarasasTitle() {
      if (this.isAdresas) {
        return this.$t('Nekilnojamojo turto paieška pagal adresą')
      }
      if (this.isRegistras) {
        return this.$t('Nekilnojamojo turto paieška pagal registrą')
      }
      return this.$t('Nekilnojamojo turto paieška pagal unikalų numerį')
    },
    isUnikalus() {
      return !this.isAdresas && !this.isRegistras
    },
    isLocal() {
      return process.env.VUE_APP_LOCAL === 'local'
    },
  },
  methods: {
    getData() {
      if (!this.canSearch) {
        return
      }

      if (this.isUnikalus && !this.validateTurIdentifikatorius()) {
        window._VMA.$emit(
          'DATA_ERROR',
          'Įvestas neteisingas unikalus numeris, patikslinkite.'
        )
        this.loading = false
        return
      }

      this.loading = true
      this.tableLoading = true
      this.isSearching = true
      this.showFail = false
      this.resetSearch()

      const data = {
        code: this.isAdresas ? parseInt(this.adresasSelect.code) : null,
        reg_nr: this.isRegistras ? this.registrasSelect : null,
        tur_identifikatorius: this.isUnikalus ? this.unikalusSelect : null,
      }

      this.$axios
        .post(
          `ntrpaslaugos/prasymas/${this.prasymasID}/turtas/pirmenybe-sarasas`,
          data
        )
        .then(res => {
          this.turtai = res.data.turtai
          this.pasirinktosDalys = res.data.pasirinktos_dalys
          this.ivestosDalys = res.data.pasirinktos_dalys
          this.setItems(res.data.pasirinkti_amnids)
          this.checkAutoSave()
        })
        .catch(err => {
          this.failMsg = err.response.data.message
          this.showFail = true
          window._VMA.$emit('DATA_ERROR', this.failMsg)
        })
        .finally(() => {
          this.tableLoading = false
          this.loading = false
          this.isSearching = false
        })
    },
    saveData() {
      this.loading = true

      let pasirinkti_turtai = []
      let pasirinktas_dokumentas = null

      this.turtai.forEach(turtas => {
        let pasirinktos_dalys = []
        let nuzymetos_dalys = []

        turtas.dalys.forEach(dalis => {
          if (this.pasirinktosDalys.includes(dalis.dal_value)) {
            pasirinktos_dalys.push(dalis.dal_value)
          }
          // if (this.nuzymetosDalys.includes(dalis.dal_value)) {
          //   nuzymetos_dalys.push(dalis.dal_value)
          // }
        })

        if (pasirinktos_dalys.length > 0 || nuzymetos_dalys.length > 0) {
          pasirinkti_turtai.push({
            tur_identifikatorius: turtas.tur_identifikatorius,
            dalys: pasirinktos_dalys,
            // nuzymetos_dalys: nuzymetos_dalys,
          })
        }
      })

      const data = {
        pasirinkti_turtai: pasirinkti_turtai,
      }

      this.$axios
        .post(`ntrpaslaugos/prasymas/${this.prasymasID}/turtas/pirmenybe`, data)
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
          this.$emit('updateTurtasList')
          this.closeModal()
        })
        .catch(err => {
          this.$emit('updateTurtasList', err.response.data.message)
          this.closeModal()
          // window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    setItems(amn_ids) {
      this.turtai.forEach(turtas => {
        turtas.dalys.forEach(dalis => {
          dalis.enabled =
            this.pasirinktosDalys.length == 0 ||
            (JSON.stringify(dalis.amn_ids) == JSON.stringify(amn_ids) &&
              !this.ivestosDalys.includes(dalis.dal_value))
        })
      })
    },
    checkItem(p_dalis) {
      // if (this.pasirinktosDalys.includes(p_dalis.dal_id)) {
      //   this.nuzymetosDalys = this.nuzymetosDalys.filter(
      //     item => item !== p_dalis.dal_id
      //   )
      // }
      // if (!this.pasirinktosDalys.includes(p_dalis.dal_id)) {
      //   this.nuzymetosDalys.push(p_dalis.dal_id)
      // }

      this.turtai.forEach(turtas => {
        turtas.dalys.forEach(dalis => {
          dalis.enabled =
            this.pasirinktosDalys.length == 0 ||
            (JSON.stringify(dalis.amn_ids) == JSON.stringify(p_dalis.amn_ids) &&
              !this.ivestosDalys.includes(dalis.dal_value))
        })
      })
    },
    checkAutoSave() {
      if (this.turtai.length > 1) {
        return
      }
      this.turtai.forEach(turtas => {
        if (turtas.dalys.length > 1) {
          return
        }
        turtas.dalys.forEach(dalis => {
          if (!this.pasirinktosDalys.includes(dalis.dal_value)) {
            this.pasirinktosDalys.push(dalis.dal_value)
          }
          this.saveData()
        })
      })
    },
    cancelData() {
      this.cancelSearch()
      this.closeModal()
    },
    closeModal() {
      this.showModal = false
    },
    submit() {
      this.getData()
    },
    cancel() {
      this.cancelSearch()
      this.closeModal()
    },
    cancelSearch() {
      this.adresasSelect = null
      this.registrasSelect = null
      this.unikalusSelect = null
      this.showFail = false
      this.resetSearch()
    },
    resetSearch() {
      this.headerCheck = false
      this.turtai = []
      this.pasirinktosDalys = []
      this.pasirinktosTeises = []
      this.nuzymetosDalys = []
    },
    getActiveClass(item) {
      if (item.disabled) {
        return 'row-disabled'
      }
      return ''
    },
    customSearch(value, search, item) {
      return Object.values(item).some(
        v =>
          v &&
          v
            .toString()
            .toLowerCase()
            .includes(search.toLowerCase())
      )
    },
    validateTurIdentifikatorius() {
      if (this.isLocal) {
        return true
      }

      const unikalus = this.unikalusSelect
        .replaceAll('-', '')
        .replaceAll(':', '')
      return unikalus.length == 12 || unikalus.length == 16
    },
  },
}
</script>
<style lang="scss" scoped>
.modalCard {
  background: #f5f5f5 !important;
}
.modalTitle {
  background: #f5f5f5 !important;
  position: sticky;
  top: 0;
  z-index: 2;
  background: none;
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}
.modalActions {
  position: sticky;
  bottom: 0;
  z-index: 1;
}
</style>
